import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "../ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { createInvestigation } from "../../clients/metoro/investigations";
import { CreateInvestigationRequest } from "../../types/investigation";
import { withErrorHandling } from "../../utility/apiUtils";
import { AlertTriangle, Circle } from "lucide-react";

// Priority configuration
const PRIORITY_CONFIG = {
  P1: { label: "Critical", color: "text-red-500", icon: AlertTriangle },
  P2: { label: "High", color: "text-orange-500", icon: AlertTriangle },
  P3: { label: "Medium", color: "text-yellow-500", icon: Circle },
  P4: { label: "Low", color: "text-blue-500", icon: Circle },
  P5: { label: "Info", color: "text-gray-500", icon: Circle }
};

interface CreateInvestigationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    title?: string;
    markdown?: string;
    tags?: Record<string, string>;
    issueStartTime?: string;
    issueEndTime?: string;
    chatHistoryUuid?: string;
  };
}

const CreateInvestigationDialog: React.FC<CreateInvestigationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialData,
}) => {
  const { toast } = useToast();
  const [title, setTitle] = useState(initialData?.title || "");
  const [markdown, setMarkdown] = useState(initialData?.markdown || "");
  const [tagInput, setTagInput] = useState("");
  const [priority, setPriority] = useState("P3");

  const mutation = useMutation({
    mutationFn: (request: CreateInvestigationRequest) =>
      withErrorHandling(() => createInvestigation(request)),
    onSuccess: () => {
      toast({
        title: "Investigation created",
        description: "The investigation has been created successfully.",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create investigation. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !markdown.trim()) {
      toast({
        title: "Validation error",
        description: "Title and content are required.",
        variant: "destructive",
      });
      return;
    }

    // Parse tags from comma-separated string
    const tags: Record<string, string> = {};
    if (tagInput.trim()) {
      tagInput.split(",").forEach((tag) => {
        const [key, value] = tag.split(":").map((s) => s.trim());
        if (key && value) {
          tags[key] = value;
        }
      });
    }
    
    // Add priority to tags
    tags.priority = priority;

    const request: CreateInvestigationRequest = {
      title,
      markdown,
      tags: Object.keys(tags).length > 0 ? tags : undefined,
      issueStartTime: initialData?.issueStartTime,
      issueEndTime: initialData?.issueEndTime,
      chatHistoryUuid: initialData?.chatHistoryUuid,
    };

    mutation.mutate(request);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-backgroundmedium border-border">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-textlight">Create Investigation</DialogTitle>
            <DialogDescription className="text-textdark">
              Create a new investigation to document and track an issue.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <div className="grid gap-2">
              <Label htmlFor="title" className="text-textmedium">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the investigation"
                className="bg-backgroundlight border-border text-textmedium placeholder:text-textdark"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="markdown" className="text-textmedium">Investigation Details</Label>
              <Textarea
                id="markdown"
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                placeholder="Detailed description of the issue, findings, and actions taken..."
                className="min-h-[200px] bg-backgroundlight border-border text-textmedium placeholder:text-textdark"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="priority" className="text-textmedium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="bg-backgroundlight border-border text-textmedium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-backgroundmedium border-border">
                  {Object.entries(PRIORITY_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key} className="text-textmedium hover:bg-backgroundlight">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${config.color}`} />
                          <span>{key} - {config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tags" className="text-textmedium">Additional Tags (optional)</Label>
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="service:api, component:database"
                className="bg-backgroundlight border-border text-textmedium placeholder:text-textdark"
              />
              <p className="text-sm text-textdark">
                Enter tags as key:value pairs separated by commas
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-border text-textmedium hover:bg-border/20"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={mutation.isPending}
              className="bg-primary text-white hover:bg-primary/90"
            >
              {mutation.isPending ? "Creating..." : "Create Investigation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvestigationDialog;